import { phoneKey } from '../documents/emergency';
import { fill, t } from '../strings';
import { wallClockInZone } from '../zonedTime';
import type { CallStatus } from './bridgedCall';
import type { TextThreadRow } from '../identity/textThread';

/**
 * The phone system's server half (piece 21, *Communications seam*; decisions 11780 to 11783): the organization's
 * numbers, the reads Recents, Voicemail and the Texts conversations answer, and the phone's business hours. The
 * Twilio card, the web's pages (batch TW6) and the phone's screens (batch TW8) read these shapes.
 */

/** 🔒 Decision 11780: the General number, shared, or one rep's own. */
export const PHONE_NUMBER_KINDS = ['General', 'Rep'] as const;
export type PhoneNumberKind = (typeof PHONE_NUMBER_KINDS)[number];

/** One of the organization's numbers as the Twilio card lists it. */
export interface PhoneNumberRow {
  id: string;
  number: string;
  kind: PhoneNumberKind;
  personId: string | null;
  personName: string | null;
  forwardingNumber: string | null;
  isActive: boolean;
}

export interface PhoneNumbersAnswer {
  rows: PhoneNumberRow[];
}

/** What + Add Number and Edit send; the forwarding cell is an administrator's alone (the owner's P1c). */
export interface PhoneNumberInput {
  number: string;
  kind: PhoneNumberKind;
  personId?: string | null;
  forwardingNumber?: string | null;
}

/** 🔨 The paths are relative to the API root (piece 1). */
export const TWILIO_NUMBERS_PATH = '/settings/integrations/twilio/numbers';
export function twilioNumberPath(id: string): string {
  return `${TWILIO_NUMBERS_PATH}/${encodeURIComponent(id)}`;
}
export function twilioNumberRetirePath(id: string): string {
  return `${twilioNumberPath(id)}/retire`;
}

/** 🔒 Decision 11782: Recents, the Voicemail list, and the phone app's two routes for row T2675. */
export const CALLS_PATH = '/calls';
export const CALLS_VOICEMAIL_PATH = '/calls/voicemail';
export const CALLS_TOKEN_PATH = '/calls/token';
export const CALLS_REGISTER_PATH = '/calls/register';

/** The rows a page of Recents carries unless the reader asks for fewer, and the most it ever carries. */
export const RECENT_CALLS_PAGE_SIZE = 50;
export const RECENT_CALLS_PAGE_MAX = 200;

/** One call as Recents lists it: an inbound call from an unknown number names no record and reads by its number. */
export interface RecentCallRow {
  id: string;
  direction: 'Inbound' | 'Outbound';
  status: CallStatus;
  /** The caller's number on an inbound call, the caller ID on an outbound one. */
  fromNumber: string | null;
  toNumber: string;
  phoneNumberId: string | null;
  recordType: string | null;
  recordId: string | null;
  recordName: string | null;
  /** When the call started, an ISO instant. */
  at: string;
  /** When the call ended, an ISO instant; empty while it rings (decision 11860, for the call's window). */
  endedAt: string | null;
  durationSeconds: number | null;
  /** The recording's address at the provider and its transcript, a voicemail's on an inbound row (decision 11860). */
  recordingUrl: string | null;
  transcript: string | null;
  /** The Log Contact note the call wrote, whose record's page a row opens (decision 11860). */
  noteId: string | null;
  /** The to-do a missed call or a voicemail raised, and whether it is still open, for Done (decision 11860). */
  todoId: string | null;
  todoOpen: boolean;
}

/** A Voicemail row carries what a Recents row carries; the recording and the transcript are its own (decision 11782). */
export type VoicemailRow = RecentCallRow;

/** 🔒 Decision 11860: one of the organization's numbers whose calls the reader reads, for the number filter. */
export interface ReadableNumber {
  id: string;
  number: string;
  kind: PhoneNumberKind;
}

export interface RecentCallsAnswer {
  rows: RecentCallRow[];
  total: number;
  page: number;
  pageSize: number;
  /** 🔒 Decision 11860: the numbers the reader reads calls on, active first, for the filter. */
  numbers: ReadableNumber[];
}

export interface VoicemailAnswer {
  rows: VoicemailRow[];
  total: number;
  page: number;
  pageSize: number;
  numbers: ReadableNumber[];
}

/** 🔒 Decision 11782: the Texts conversations, one row per number per conversation. */
export const TEXT_CONVERSATIONS_PATH = '/texts/conversations';
export function textConversationPath(theirNumber: string, ourNumber: string): string {
  return `${TEXT_CONVERSATIONS_PATH}/${encodeURIComponent(theirNumber)}?on=${encodeURIComponent(ourNumber)}`;
}

export interface ConversationRow {
  ourNumber: string;
  theirNumber: string;
  latestBody: string;
  latestAt: string;
  latestDirection: 'Inbound' | 'Outbound';
  /** The inbound texts since our last reply. */
  unanswered: number;
  personId: string | null;
  personName: string | null;
}

export interface ConversationsAnswer {
  rows: ConversationRow[];
}

export interface ConversationThread {
  ourNumber: string;
  theirNumber: string;
  personId: string | null;
  personName: string | null;
  /** Whether a reply can be sent: a text adapter is active and one Person holds their number. */
  replies: boolean;
  rows: TextThreadRow[];
}

/** A phone the app registers to answer calls in the app (row T2675). */
export interface CallRegistrationInput {
  deviceToken: string;
  platform: 'Ios' | 'Android';
}

/** 🔒 Decision 12000: what `POST calls/token` names, the phone's platform, so the grant carries that platform's push credential. */
export interface VoiceTokenRequest {
  platform: 'Ios' | 'Android';
}

/** 🔒 Decision 12000: the Voice SDK's access token, the identity it names and when it expires (an instant, ISO 8601). */
export interface VoiceTokenAnswer {
  token: string;
  identity: string;
  expiresAt: string;
}

/** 🔒 Decision 12000: an access token lasts one hour. */
export const VOICE_TOKEN_SECONDS = 3600;
/** 🔒 Decision 12000: a ring reaches the registrations seen in the last thirty days. */
export const CALL_REGISTRATION_DAYS = 30;

/** 🔒 Decision 12000: a Person's identity in the Voice SDK, the id with its hyphens written as underscores (Twilio holds an identity to letters, digits and underscores). */
export function voiceIdentityOf(personId: string): string {
  return personId.replace(/-/g, '_');
}

/** The Person id a Voice SDK identity names, or a client address `client:` and the identity; null where it names none. */
export function personOfVoiceIdentity(identity: string | null | undefined): string | null {
  const bare = (identity ?? '').trim().replace(/^client:/, '');
  const id = bare.replace(/_/g, '-');
  return /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i.test(id) ? id.toLowerCase() : null;
}

/** 🔒 Decision 11783: one open day, its times in the organization's zone, the fields the work calendar's row carries. */
export interface PhoneHoursEntry {
  dayOfWeek: number;
  opensAt: string;
  closesAt: string;
}

const TIME = /^([01][0-9]|2[0-3]):[0-5][0-9]$/;
const isClose = (value: string) => value === '24:00' || TIME.test(value);

/** The refusal a stored hours value earns, or null; a blank clears the row and the phone reads as always open. */
export function phoneHoursProblem(value: unknown): string | null {
  if (value === null || value === undefined) return null;
  if (!Array.isArray(value)) return t('calls.error.hoursList');
  const days = new Set<number>();
  for (const entry of value as unknown[]) {
    const row = (entry ?? {}) as Partial<PhoneHoursEntry>;
    if (typeof row.dayOfWeek !== 'number' || !Number.isInteger(row.dayOfWeek) || row.dayOfWeek < 0 || row.dayOfWeek > 6) return t('calls.error.hoursWeekday');
    if (days.has(row.dayOfWeek)) return t('calls.error.hoursOnce');
    days.add(row.dayOfWeek);
    if (typeof row.opensAt !== 'string' || !TIME.test(row.opensAt) || typeof row.closesAt !== 'string' || !isClose(row.closesAt)) return t('calls.error.hoursTimes');
    if (row.closesAt !== '24:00' && row.closesAt <= row.opensAt) return t('calls.error.hoursOrder');
  }
  return null;
}

/** The stored value read as hours; anything that is not one reads as unset. */
export function phoneHoursOf(value: unknown): PhoneHoursEntry[] | null {
  if (value === null || value === undefined || phoneHoursProblem(value) !== null) return null;
  return (value as PhoneHoursEntry[]).map((row) => ({ dayOfWeek: row.dayOfWeek, opensAt: row.opensAt, closesAt: row.closesAt }));
}

/**
 * 🔒 Decision 11783: whether an instant falls inside the phone's hours, read in the organization's zone through the
 * shared zoned helpers. Unset hours read as always open; a day with no entry is closed.
 */
export function insidePhoneHours(hours: readonly PhoneHoursEntry[] | null, at: Date, zone: string): boolean {
  if (hours === null) return true;
  const wall = wallClockInZone(at, zone);
  const dayOfWeek = new Date(`${wall.slice(0, 10)}T00:00:00Z`).getUTCDay();
  const time = wall.slice(11, 16);
  const day = hours.find((row) => row.dayOfWeek === dayOfWeek);
  return day !== undefined && time >= day.opensAt && (day.closesAt === '24:00' || time < day.closesAt);
}

/** 🔒 Decision 11783: the two timings, whole seconds from 5 to 120. */
export const RING_SECONDS_DEFAULT = 30;
export const REP_FIRST_SECONDS_DEFAULT = 15;
export const RING_SECONDS_MIN = 5;
export const RING_SECONDS_MAX = 120;

export function ringSecondsProblem(value: unknown): string | null {
  if (value === null || value === undefined || value === '') return null;
  if (typeof value !== 'number' || !Number.isInteger(value) || value < RING_SECONDS_MIN || value > RING_SECONDS_MAX) return `A ring lasts a whole number of seconds from ${RING_SECONDS_MIN} to ${RING_SECONDS_MAX}.`;
  return null;
}

/** The longest greeting or menu sentence Twilio is asked to read. */
export const PHONE_WORDS_MAX = 500;

export function phoneWordsProblem(value: unknown): string | null {
  if (value === null || value === undefined || value === '') return null;
  if (typeof value !== 'string') return t('calls.error.wordsText');
  if (value.length > PHONE_WORDS_MAX) return fill(t('calls.error.wordsMax'), { max: PHONE_WORDS_MAX });
  return null;
}

/** Two spellings of one number compare equal by their digits (decision 11780). */
export function sameNumber(a: string | null | undefined, b: string | null | undefined): boolean {
  const left = phoneKey(a ?? '');
  return left !== '' && left === phoneKey(b ?? '');
}
